import React, { Suspense, useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import { useBodyClass } from "hooks/useBodyClass";
import { useSettings } from "providers/settings-provider";
import { Footer } from "./Header/footer";
import { Header } from "./Header/header";
import { Navbar } from "./Header/navbar";
import { BreadCrumb, Container } from "components/atoms";
import { useDispatch } from "react-redux";
import { GetMeAction } from "store/middlewares/me";
import { GetAllSettingsAction } from "store/middlewares/settings/settings";

// ─── Stable shell ────────────────────────────────────────────────────────────
// Memoized so Header / Navbar / Footer are never re-rendered on route changes.
// Only the <Outlet /> inside Layout (below) updates when the route changes.
const LayoutShell = React.memo(function LayoutShell({ headerRef }) {
  return (
    <>
      {/* fixed header – stays mounted across route changes */}
      <div ref={headerRef} className="fixed top-0 inset-x-0 z-20 bg-white">
        <div className="max-w-[1280px] mx-auto">
          <Header />
          <Navbar />
        </div>
      </div>

      {/* spacer so content sits below the fixed header */}
      <div className="h-[135px] mb-5 lg:mb-8 shrink-0" />
    </>
  );
});

// ─── Layout ──────────────────────────────────────────────────────────────────
export function Layout() {
  const dispatch = useDispatch();
  const { setOption } = useSettings();
  const headerRef = useRef(null);

  useEffect(() => {
    setOption("layout", "demo2");
  }, [setOption]);

  useBodyClass(`
    [--header-height-default:100px]  
    data-[sticky-header=on]:[--header-height:60px]
    [--header-height:var(--header-height-default)]	
  `);

  useEffect(() => {
    dispatch(GetMeAction());
    dispatch(GetAllSettingsAction());
  }, [dispatch]);

  // Keep the fixed header from shifting when a menu/modal locks page scrolling.
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    let scrollbar = window.innerWidth - root.clientWidth;

    const update = () => {
      const locked =
        body.hasAttribute("data-scroll-locked") ||
        body.style.overflow === "hidden";
      if (!locked) scrollbar = window.innerWidth - root.clientWidth;
      if (headerRef.current) {
        headerRef.current.style.paddingRight = locked
          ? `${scrollbar}px`
          : "0px";
      }
    };

    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(root);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(body, { attributes: true });
    window.addEventListener("resize", update);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div className="flex grow flex-col min-h-screen max-w-[1280px] mx-auto">
      {/* LayoutShell is memoized – Header/Navbar/Footer never re-render on route change */}
      <LayoutShell headerRef={headerRef} />

      <main className="grow" role="content">
        <Container>
          <BreadCrumb />
          {/* Suspense boundary scoped to just the page outlet — Header/Footer never flicker */}
          <Suspense>
            <Outlet />
          </Suspense>
        </Container>
      </main>

      <Footer />
    </div>
  );
}